import { isCanonicalProvinceCode, normalizeProvinceCode } from './provinceCode';
import { resolveProvinceName } from './provinceLookup';

export type TemperatureCoverageVariant = 'all' | 'single' | 'multiple';

export interface TemperatureCoverage {
  show: boolean;
  variant: TemperatureCoverageVariant;
  provinceNames: string[];
  inlineNames: string[];
  overflowCount: number;
}

interface CoverageAttributes {
  temperature_control?: string | boolean;
  temperature_provinces?: string[] | null;
}

interface ProvinceRow {
  province_code?: string | null;
  province_en_name?: string | null;
  province?: string | null;
}

const DEFAULT_INLINE_LIMIT = 3;

const isEnabled = (flag?: string | boolean): boolean => flag === true || flag === 'true';

/**
 * Every REAL province, localized and deduped. A carrier with `variant: 'all'`
 * cold-chains to all of them, and the carriers-page chip names them rather than
 * saying a vague "all provinces".
 *
 * The `regions` feed is not a province list — it is a city list whose
 * `province_code` column is polluted with faker values ('West Salvadorstad',
 * 'Abbottberg', …) that carry real-looking names. Only canonical governorate
 * codes survive; without this filter the popover rendered 1760 chips.
 *
 * Deliberately NOT folded into getTemperatureCoverage(): that function's `all`
 * variant intentionally returns an empty provinceNames, and other callers
 * (ShippingRuleBuilderModal) rely on that to render their own short label.
 */
export function listAllProvinceNames(
  provinceRows: ProvinceRow[] = [],
  locale: 'en' | 'ar' = 'en',
): string[] {
  const seenCodes = new Set<string>();
  const seenNames = new Set<string>();
  const names: string[] = [];

  for (const row of provinceRows) {
    if (!isCanonicalProvinceCode(row.province_code)) continue;

    const norm = normalizeProvinceCode(row.province_code) as string;
    if (seenCodes.has(norm)) continue;

    const name = resolveProvinceName(row.province_code ?? undefined, [row], locale);
    // Two codes resolving to one display name would collide as React keys.
    if (!name || seenNames.has(name)) continue;

    seenCodes.add(norm);
    seenNames.add(name);
    names.push(name);
  }

  return names.sort((a, b) => a.localeCompare(b));
}

export function getTemperatureCoverage(
  attributes: CoverageAttributes | null | undefined,
  provinceRows: ProvinceRow[] = [],
  locale: 'en' | 'ar' = 'en',
  inlineLimit: number = DEFAULT_INLINE_LIMIT,
): TemperatureCoverage {
  const hidden: TemperatureCoverage = {
    show: false,
    variant: 'all',
    provinceNames: [],
    inlineNames: [],
    overflowCount: 0,
  };
  if (!attributes || !isEnabled(attributes.temperature_control)) return hidden;

  const codes = (attributes.temperature_provinces ?? []).filter(
    (c): c is string => typeof c === 'string' && c.trim() !== '',
  );

  // enabled + no codes = serves all provinces
  if (codes.length === 0) {
    return { show: true, variant: 'all', provinceNames: [], inlineNames: [], overflowCount: 0 };
  }

  // normalized region code -> localized name (regions may use legacy 3-letter codes)
  const nameByCode = new Map<string, string>();
  for (const row of provinceRows) {
    const norm = normalizeProvinceCode(row.province_code);
    if (!norm || nameByCode.has(norm)) continue;
    const name = resolveProvinceName(row.province_code ?? undefined, [row], locale);
    if (name) nameByCode.set(norm, name);
  }

  // resolve carrier codes, dedupe by normalized code
  const seen = new Set<string>();
  const provinceNames: string[] = [];
  for (const code of codes) {
    const norm = normalizeProvinceCode(code) ?? code.trim().toUpperCase();
    if (seen.has(norm)) continue;
    seen.add(norm);
    provinceNames.push(nameByCode.get(norm) ?? norm);
  }
  provinceNames.sort((a, b) => a.localeCompare(b));

  if (provinceNames.length === 1) {
    return {
      show: true,
      variant: 'single',
      provinceNames,
      inlineNames: provinceNames,
      overflowCount: 0,
    };
  }

  const inlineNames = provinceNames.slice(0, inlineLimit);
  return {
    show: true,
    variant: 'multiple',
    provinceNames,
    inlineNames,
    overflowCount: Math.max(0, provinceNames.length - inlineNames.length),
  };
}
